import { useLogger } from '@opengov/psc-react-logger';
import React from 'react';

/**
 * Configuration options for the unified fetch function
 */
interface FetchConfig {
  /** HTTP method to use for the request */
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  /** Request body for POST/PUT/PATCH requests */
  body?: string | FormData | { query: string; variables?: { [key: string]: string } };
  /** Additional request options to merge with defaults */
  options?: RequestInit;
  /** Whether to include error handling for non-OK responses */
  includeErrorHandling?: boolean;
}

/**
 * Unified fetch function that handles both REST API and GraphQL requests
 * @param apiUrl - The URL to fetch from
 * @param authBearerToken - Optional Bearer token for authentication
 * @param config - Configuration options for the request
 * @returns Promise resolving to the parsed JSON response
 */
export const authenticatedApiFetch = async <T>(
  apiUrl: string,
  authBearerToken?: string,
  config: FetchConfig = {}
): Promise<T> => {
  const { method = 'GET', body, options = {}, includeErrorHandling = true } = config;

  // Handle GraphQL request objects by stringifying them
  const processedBody: BodyInit | undefined =
    body && typeof body === 'object' && 'query' in body ? JSON.stringify(body) : (body as BodyInit | undefined);

  const requestInit: RequestInit = {
    method,
    mode: 'cors',
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      ...(authBearerToken && { Authorization: `Bearer ${authBearerToken}` })
    },
    ...(processedBody && { body: processedBody }),
    ...options
  };

  return fetch(apiUrl, requestInit).then(async (response) => {
    if (includeErrorHandling && !response.ok) {
      throw new Error(`Failed to fetch: ${response.status} ${response.statusText}`);
    }
    return response.json();
  });
};

/**
 * Hook for managing fetch state and lifecycle
 */
export const useFetchState = () => {
  const logger = useLogger();
  const isMounted = React.useRef(true);
  const isFetchingData = React.useRef(false);

  React.useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
    };
  }, []);

  const [loading, setLoading] = React.useState(false);
  const [loadingError, setLoadingError] = React.useState(false);

  const resetFetchState = React.useCallback(() => {
    setLoading(false);
    setLoadingError(false);
  }, []);

  const setFetching = React.useCallback((isFetching: boolean) => {
    isFetchingData.current = isFetching;
  }, []);

  const handleFetchError = React.useCallback(
    (error: unknown, context: string) => {
      if (!isMounted.current) return;

      setLoadingError(true);
      setLoading(false);
      logger.error(`Error in ${context}`, { error });
    },
    [logger]
  );

  const handleFetchSuccess = React.useCallback(() => {
    if (!isMounted.current) return;

    setLoading(false);
    setLoadingError(false);
  }, []);

  return {
    isMounted,
    isFetchingData,
    loading,
    loadingError,
    resetFetchState,
    setFetching,
    handleFetchError,
    handleFetchSuccess,
    setLoading,
    setLoadingError
  };
};
